import { Link, useLocation } from 'react-router-dom';

import { LazyImage } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAuth } from '@/pages/auth';

interface LogoProps {
  /** `full` = wordmark logo (nav); `small` = compact mark (mobile). */
  variant?: 'full' | 'small';
  className?: string;
}

const PUBLIC_PATH_PREFIXES = ['/blogs', '/news', '/live-tv', '/places', '/legal', '/about', '/contact', '/login', '/register', '/forgotpassword'];

/**
 * Brand logo that links home (or to the dashboard when authenticated).
 */
export function Logo({ variant = 'full', className }: LogoProps) {
  const authenticated = useAuth((s) => s.status === 'authenticated');
  const location = useLocation();
  // On public marketing routes the authed user opened deliberately
  // (blog reading, news, /tv, etc.) keep the brand-mark pointing home
  // rather than yanking them into the dashboard.
  const onPublicRoute = PUBLIC_PATH_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  );
  const to = authenticated && !onPublicRoute ? '/dashboard' : '/';
  const src = variant === 'small' ? '/assets/logo_small.png' : '/assets/logo.png';
  // Natural pixel dimensions verified via `file` against the shipped PNGs.
  // Browser uses these to reserve horizontal space BEFORE the PNG header
  // arrives — without them, the topbar Search field + right-side icons
  // reflow when the logo lands (CLS). The ratio MUST match the real file
  // or the rendered width will be wrong; update here if the assets change.
  const dims = variant === 'small'
    ? { width: 34, height: 30 }    // logo_small.png is 34x30
    : { width: 171, height: 30 };  // logo.png wordmark is 171x30

  return (
    <Link
      to={to}
      aria-label="Yaarikut home"
      className={cn('inline-flex shrink-0 items-center', className)}
    >
      <LazyImage
        src={src}
        alt="Yaarikut"
        skeleton={false}
        width={dims.width}
        height={dims.height}
        className={cn('w-auto', variant === 'small' ? 'h-8' : 'h-6')}
      />
    </Link>
  );
}
